import { hasUsablePosition } from '../lib/meshview';
import type { HeardByEntry, NodeDetail, NodeSighting, TelemetryHistoryResponse, NodeNeighborsResponse, AuditNodeEntry, AuditNodesResponse, NodeNeighbor, Packet } from '../api/types';
import { useAppData } from '../state/AppData';
import { useAuth } from '../state/AuthContext';
import { useState, useEffect, useMemo } from 'react';
import { tryApiGet } from '../api/client';
import { parseTimestamp, formatNumber, relativeTime, formatUptimeSincePowerUp, formatClockOffset, formatSampleSpan, formatClockDrift, formatDateOnly, clockTime } from '../lib/format';
import { isStationaryTrack, type NeighborTier, neighborTier, PositionHistoryMap } from '../components/PositionHistoryMap';
import { navigate } from '../lib/router';
import { RetiredNodeNotice, RetireNodeButton } from '../components/RetiredNodesList';
import { resolveNodeProtocol } from '../lib/nodeProtocol';
import { roleInfo, UNKNOWN_PROTOCOL_LABEL, PROTOCOL_LABEL, packetCategoryInfo } from '../lib/definitions/protocols';
import { freshnessOf, FRESHNESS_LABEL } from '../lib/definitions/health';
import { colorFor, CHROME } from '../lib/definitions/palette';
import { NodeTagsControl } from '../components/NodeTags';
import { SectionCard } from '../components/SectionCard';
import { formatDistanceKm } from '../lib/units';
import { lastHeardText } from '../lib/packetFeed';
import { auditColorInfo } from '../lib/definitions/analysis';
import { NodeCommandsSection } from '../components/NodeCommandsSection';
import { NodeReachSection } from '../components/NodeReachSection';
import { NodeAuditSection } from '../components/NodeAuditSection';
import { NodeLocationSection } from '../components/NodeLocationSection';
import { ContactShareSection } from '../components/ContactShareSection';
import { TelemetryChart } from '../components/TelemetryChart';
import { ReconciliationSection } from '../components/ReconciliationSection';
import { ObserverBadge } from '../components/ObserverBadge';
import { MetricCard, InfoTile, NodeIdValue, batteryLabel, batterySemanticColor, formatDuration, NEIGHBOR_TIER_COLOR, NEIGHBOR_TIER_LABEL, SIGHTING_KIND_COLOR, SIGHTING_KIND_TITLE, SIGHTING_KIND_LABEL } from '../components/node-detail/presentation';
import { MeshCoreLinkTable } from '../components/node-detail/MeshCoreLinkTable';
import type { MeshCoreLinkRow } from '../components/node-detail/MeshCoreLinkTable';
import { summarizeTelemetryHistory, buildTelemetrySeries, telemetryFieldRank, displayTelemetryUnit, titleizeTelemetryField, formatTelemetryValue, chartTimeLabel, isPercentTelemetryField } from '../lib/nodeTelemetry';


// Phase 2 brief, item 3: the expanded node detail as a real, dedicated page
// (the quick side-panel from phase 1 — NodeDetailPanel.tsx — stays for a
// fast click-through from the map; this is what its own "full detail" link,
// per spec §5, goes to). Every section that depends on backend work not yet
// landed says so plainly instead of guessing or omitting silently — see
// each section's comment for exactly what was tried and what it returned.
//
// 2026-09-23 layout rework: this page was a single narrow column — a dense
// identity grid, then five sections stacked, of which Position History was
// a ~40-row text log dominating everything below it. Restructured,
// modeled on Malla's node-detail page — see the reference
// screenshots this build worked from — into: a metric row, a two-column
// layout (content cards / observer sidebar), and Position History replaced
// with an actual map. No backend/API change; every value below still comes
// from data this page already fetched before this rework.
// Position 0,0 (2026-09-26 check-fixes third pass, fix 6): thin wrapper
// around lib/meshview.ts's own `hasUsablePosition` (the Live Map's own
// null-island rule, reused rather than re-derived) that takes plain
// lat/lon values instead of an object — `hasUsablePosition`'s type
// predicate narrows its argument's TYPE to `{lat, lon}`, which would erase
// every other field (ts/observed_at/sighting_kind/observer_id/...) off
// whatever row it's called on; this avoids that entirely.
function isUsablePosition(lat: number | null | undefined, lon: number | null | undefined): boolean {
  return lat != null && lon != null && hasUsablePosition({ lat, lon });
}


function normalizeHeardBy(raw: unknown): HeardByEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => (typeof item === "string" ? { observer_id: item } : (item as HeardByEntry)));
}


export function NodeDetailPage({ id }: { id: string }) {
  const { nodes, loadNodeDetail, observerTrust, effectiveTheme, select, displaySettings } = useAppData();
  const tempPref = displaySettings?.display.temperature_unit;
  const distPref = displaySettings?.display.distance_unit;
  const { isAdmin, status: authStatus } = useAuth();
  const base = nodes.get(id);
  const [detail, setDetail] = useState<NodeDetail | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [history, setHistory] = useState<NodeSighting[] | null | "unavailable">(null);
  // GET /api/history/nodes/{protocol}/{native_id}/telemetry — exists,
  // returns {raw, hourly}, was never called from the frontend until now
  // (see summarizeTelemetryHistory above for how it's condensed).
  const [telemetryHistory, setTelemetryHistory] = useState<TelemetryHistoryResponse | null | "unavailable">(null);
  // GET /api/nodes/{protocol}/{native_id}/neighbors (2026-09-24) — direct,
  // node-to-node neighbours (NeighborInfo + meshview_direct_links), drawn
  // on the position map below. "unavailable" here means the endpoint
  // itself didn't respond (404/network) on this backend build — a
  // different fact from the response's own `available: false`, which is
  // an honest per-protocol "no neighbour data for this protocol" answer
  // (MeshCore today) and is rendered explicitly further down, not folded
  // into this sentinel.
  const [neighbors, setNeighbors] = useState<NodeNeighborsResponse | null | "unavailable">(null);
  // Clock offset/drift (2026-09-26, "Clock drift per node" board item) —
  // there is no dedicated per-node audit endpoint; this reads the SAME
  // GET /api/audit/nodes the Behaviour tab's own Nodes list uses, narrowed
  // to this one node (native_id filter), and takes its clock_* fields.
  // MeshCore-only (D1's own advert loader this metric reuses never runs
  // against a Meshtastic node) — the Clock section renders nothing for
  // any other protocol, including "unknown".
  const [auditClock, setAuditClock] = useState<AuditNodeEntry | null | "unavailable">(null);

  const protocolForHistory = base?.protocol ?? detail?.protocol;

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setLoadError(false);
    setHistory(null);
    loadNodeDetail(id).then((d) => {
      if (cancelled) return;
      if (d) setDetail(d);
      else setLoadError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [id, loadNodeDetail]);

  // The real endpoint is keyed by (protocol, native_id), not the bare id —
  // /api/history/nodes/{protocol}/{native_id} (confirmed via the backend's
  // own OpenAPI schema, 2026-09-20) — so it can't be requested until we
  // know the node's protocol, which comes from the roster (`base`) or the
  // detail fetch above, whichever resolves first.
  useEffect(() => {
    if (!protocolForHistory) return;
    let cancelled = false;
    tryApiGet<NodeSighting[]>(`/api/history/nodes/${encodeURIComponent(protocolForHistory)}/${encodeURIComponent(id)}?limit=200`)
      .then((h) => !cancelled && setHistory(h ?? "unavailable"))
      .catch(() => !cancelled && setHistory("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [id, protocolForHistory]);

  useEffect(() => {
    if (!protocolForHistory) return;
    let cancelled = false;
    setTelemetryHistory(null);
    tryApiGet<TelemetryHistoryResponse>(
      `/api/history/nodes/${encodeURIComponent(protocolForHistory)}/${encodeURIComponent(id)}/telemetry`,
    )
      .then((t) => !cancelled && setTelemetryHistory(t ?? "unavailable"))
      .catch(() => !cancelled && setTelemetryHistory("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [id, protocolForHistory]);

  useEffect(() => {
    if (!protocolForHistory) return;
    let cancelled = false;
    setNeighbors(null);
    tryApiGet<NodeNeighborsResponse>(`/api/nodes/${encodeURIComponent(protocolForHistory)}/${encodeURIComponent(id)}/neighbors`)
      .then((r) => !cancelled && setNeighbors(r ?? "unavailable"))
      .catch(() => !cancelled && setNeighbors("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [id, protocolForHistory]);

  useEffect(() => {
    if (protocolForHistory !== "meshcore") {
      setAuditClock(null);
      return;
    }
    let cancelled = false;
    tryApiGet<AuditNodesResponse>(`/api/audit/nodes?native_id=${encodeURIComponent(id)}&protocol=meshcore&limit=1`)
      .then((r) => !cancelled && setAuditClock(r && r.nodes.length > 0 ? r.nodes[0] : "unavailable"))
      .catch(() => !cancelled && setAuditClock("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [id, protocolForHistory]);

  const node = detail ?? base;

  const historyList = useMemo(() => (Array.isArray(history) ? history : []), [history]);

  // Position points: the subset of history rows that actually carry a
  // position — some sightings (a NODEINFO-only reception, say) never had
  // one. Sorted oldest-first so the "span" and "most recent" summary below
  // can be computed directly off the ends of this one array.
  const positionPointsSorted = useMemo(() => {
    return historyList
      .filter((p) => isUsablePosition(p.lat, p.lon))
      .slice()
      .sort((a, b) => (parseTimestamp(a.ts ?? a.observed_at) ?? 0) - (parseTimestamp(b.ts ?? b.observed_at) ?? 0));
  }, [historyList]);

  const positionSpanMs =
    positionPointsSorted.length > 1
      ? (parseTimestamp(positionPointsSorted[positionPointsSorted.length - 1].ts ?? positionPointsSorted[positionPointsSorted.length - 1].observed_at) ?? 0) -
        (parseTimestamp(positionPointsSorted[0].ts ?? positionPointsSorted[0].observed_at) ?? 0)
      : 0;
  const positionRosterCount = positionPointsSorted.filter((p) => p.sighting_kind === "roster_snapshot").length;
  // Most recent few, newest first — the whole point of replacing the old
  // ~40-row log with the map is to stop dumping every row on screen.
  const recentPositionPoints = positionPointsSorted.slice(-5).reverse();

  // A router that has never moved reports the same coordinate every time —
  // see PositionHistoryMap's own isStationaryTrack for the epsilon and why
  // a zero-area fitBounds reads as broken rather than "hasn't moved". Used
  // here too, for the summary line below the map (Change 2 of the
  // 2026-09-23 Commands/reorder pass).
  const positionStationary = useMemo(
    () => isStationaryTrack(positionPointsSorted.map((p) => ({ lat: p.lat as number, lon: p.lon as number }))),
    [positionPointsSorted],
  );

  // Neighbours: available only once the endpoint has resolved with
  // available=true (MeshCore nodes get available=false with a `reason` —
  // rendered honestly in the Position card below, not treated the same as
  // "endpoint unreachable" or "zero neighbours"). Split here into
  // positioned (drawable on the map) vs. not — the latter is still a real
  // fact ("we know this link exists but cannot place it") and gets its
  // own list under the map rather than being dropped.
  const neighborList: NodeNeighbor[] = neighbors && neighbors !== "unavailable" && neighbors.available ? neighbors.neighbors : [];
  const positionedNeighbors = useMemo(() => neighborList.filter((n) => isUsablePosition(n.lat, n.lon)), [neighborList]);
  // 2026-09-26 board item ("Estimated position for nodes without one") —
  // only ever set when THIS node itself has no usable position (backend
  // contract, EstimatedPosition's own docstring). Used below to still
  // render the map (centred on the estimate, shaded circle, no pin)
  // instead of falling into the "no sightings" empty state a node with
  // no real history/position would otherwise hit.
  const estimatedPosition = detail?.estimated_position ?? null;
  const unplacedNeighbors = useMemo(() => neighborList.filter((n) => !isUsablePosition(n.lat, n.lon)), [neighborList]);
  const neighborTierCounts = useMemo(() => {
    const counts: Record<NeighborTier, number> = { bidirectional: 0, "one-way": 0, "observed-only": 0 };
    for (const n of neighborList) counts[neighborTier(n)]++;
    return counts;
  }, [neighborList]);

  // MeshCore neighbours section (2026-09-26 board item) — flattened out of
  // neighborList's own meshcore_direct_links (one entry per neighbour, but
  // a neighbour can carry evidence in both directions, or more than one
  // row per direction — different source/resolution, migration 0036's own
  // UNIQUE constraint) into two flat "hears"/"heard by" row lists, one row
  // per link entry rather than per neighbour, so a neighbour with multiple
  // distinguishable evidence rows shows all of them.
  const { meshcoreHears, meshcoreHeardBy } = useMemo(() => {
    const hears: MeshCoreLinkRow[] = [];
    const heardBy: MeshCoreLinkRow[] = [];
    for (const n of neighborList) {
      for (const link of n.meshcore_direct_links) {
        const row: MeshCoreLinkRow = { neighbor: n, link };
        if (link.transmitter_is_this_node) heardBy.push(row);
        else hears.push(row);
      }
    }
    return { meshcoreHears: hears, meshcoreHeardBy: heardBy };
  }, [neighborList]);

  const telemetrySummary =
    telemetryHistory && telemetryHistory !== "unavailable" ? summarizeTelemetryHistory(telemetryHistory) : null;
  const telemetrySeries =
    telemetryHistory && telemetryHistory !== "unavailable" ? buildTelemetrySeries(telemetryHistory) : null;
  const telemetrySeriesSorted = telemetrySeries
    ? Array.from(telemetrySeries.entries()).sort(
        ([a], [b]) => telemetryFieldRank(a) - telemetryFieldRank(b) || a.localeCompare(b),
      )
    : [];

  if (!node) {
    return (
      <div className="node-detail-page">
        <button className="back-link" onClick={() => navigate("#/nodes")}>
          ← Back to node list
        </button>
        <div className="empty-state">Node {id} not found in the current roster.</div>
        {isAdmin && <RetiredNodeNotice nativeId={id} />}
      </div>
    );
  }

  const protocol = resolveNodeProtocol(node);
  const role = protocol === "unknown" ? null : roleInfo(protocol, node.role);
  const fresh = freshnessOf(node.last_heard);
  const name = node.long_name || node.short_name || String(node.id);
  const heardBy = normalizeHeardBy(detail?.heard_by ?? node.observer_ids ?? []);
  const csStations = detail?.corescope_heard_by ?? [];
  const recentPackets: Packet[] = detail?.recent_packets ?? [];

  // One-row-per-transmission grouping was attempted here (2026-09-23) and
  // deliberately NOT done — reported back rather than guessed at. The
  // obvious key, PacketResponse.hash (packets.hash, the packets table's
  // own primary key — see api/schemas.py / api/routes/nodes.py), is
  // present on every row but is NOT stable across observers for the same
  // over-the-air transmission: ingest/hashing.py's content_hash includes
  // the packet's `channel`, and a direct RF reception vs. the same
  // transmission relayed over MQTT consistently carry different local
  // channel indices (observed live: channel 0 for this node's first-party
  // observer, channel 243 for its MQTT relay) — so the same transmission
  // gets two different hashes, one per observer, defeating hash-based
  // grouping. The only fields that DO match across such a pair — p.ts, or
  // payload.telemetry.time — are exactly the "heuristic like matching
  // timestamps" this task's own brief said not to group on. Left
  // ungrouped pending a decision (most likely a backend fix to
  // content_hash itself, out of scope for a presentation-only pass).

  // --- Metric row (§1 of the rework) --------------------------------
  // Every card here is a value already on this page from data already
  // fetched — nothing new is requested. A card is omitted outright, never
  // shown as a dash, when the underlying value is genuinely unavailable
  // (e.g. heardBy hasn't loaded/has no entries — that's not the same claim
  // as "confirmed zero observers", so neither Sightings nor Observers
  // renders in that case; the sidebar's own empty-state says so directly).
  const heardByWithCounts = heardBy.filter((h) => h.count != null);
  const sightingsSum = heardByWithCounts.reduce((acc, h) => acc + (h.count ?? 0), 0);
  const metrics: { value: string; label: string }[] = [];
  if (heardByWithCounts.length > 0) metrics.push({ value: formatNumber(sightingsSum), label: "Sightings" });
  if (heardBy.length > 0) metrics.push({ value: formatNumber(heardBy.length), label: "Observers" });
  if (node.hops != null) metrics.push({ value: String(node.hops), label: node.hops === 1 ? "Hop" : "Hops" });
  if (node.first_seen) metrics.push({ value: relativeTime(node.first_seen), label: "First seen" });

  return (
    <div className="node-detail-page">
      <button className="back-link" onClick={() => navigate("#/nodes")}>
        ← Back to node list
      </button>

      <div className="node-detail-header">
        <h2>{name}</h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span className="badge" style={{ background: "var(--ink-muted)" }}>
            {protocol === "unknown" ? UNKNOWN_PROTOCOL_LABEL : PROTOCOL_LABEL[protocol]}
          </span>
          {role && (
            <span className="badge" style={{ background: colorFor(role.color, "light") }}>
              {role.label}
            </span>
          )}
          <span className="badge" style={{ background: "var(--ink-muted)" }}>
            {FRESHNESS_LABEL[fresh]}
          </span>
        </div>
      </div>

      <NodeTagsControl
        protocol={protocol}
        nativeId={id}
        tags={base?.tags ?? detail?.tags ?? []}
        canEdit={protocol !== "unknown" && (authStatus === "authenticated" || authStatus === "not_required")}
      />

      {isAdmin && protocol !== "unknown" && (
        <div style={{ margin: "8px 0" }}>
          <RetireNodeButton protocol={protocol} nativeId={id} />
        </div>
      )}

      {loadError && (
        <div className="node-detail-note">Extended detail (recent packets, heard-by) unavailable — showing roster data only.</div>
      )}

      {metrics.length > 0 && (
        <div className="node-detail-metrics">
          {metrics.map((m) => (
            <MetricCard key={m.label} value={m.value} label={m.label} />
          ))}
        </div>
      )}

      <div className="node-detail-layout">
        <div className="node-detail-main">
          <SectionCard title="Node identity">
            {/* Card-grid-of-tiles layout, matching MeshMonitor's own node-detail
                view (reference screenshots reviewed 2026-09-23) — one fact per
                tile rather than a dotted label/value row. Every value here is
                the same data the old row layout showed; see InfoTile/
                batterySemanticColor above for the (new, this build's own)
                semantic-color thresholds and why Role/Hops stay neutral. */}
            <div className="node-detail-tiles">
              <InfoTile label="Node ID" value={<NodeIdValue id={String(node.id)} />} secondary={node.num != null ? String(node.num) : undefined} theme={effectiveTheme} />
              <InfoTile label="Short name" value={node.short_name} theme={effectiveTheme} />
              <InfoTile label="Role" value={role?.label} theme={effectiveTheme} />
              <InfoTile label="Hardware" value={node.hw_model} theme={effectiveTheme} />
              <InfoTile label="Firmware" value={node.firmware} theme={effectiveTheme} />

              {/* battery_level above 100 means powered/plugged in, not an
                  over-100% battery — see batteryLabel's own comment; that case
                  stays neutral (batterySemanticColor returns undefined for it). */}
              <InfoTile
                label="Battery"
                value={batteryLabel(node.battery_level)}
                secondary={node.voltage != null ? `(${node.voltage.toFixed(2)}V)` : undefined}
                color={batterySemanticColor(node.battery_level)}
                theme={effectiveTheme}
              />
              <InfoTile label="Hops" value={node.hops != null ? String(node.hops) : null} theme={effectiveTheme} />

              <div className="node-detail-tile">
                <div className="node-detail-tile-label">Position</div>
                {isUsablePosition(node.lat, node.lon) ? (
                  <>
                    <div className="node-detail-tile-value">
                      {node.lat!.toFixed(4)}, {node.lon!.toFixed(4)}
                    </div>
                    {(node.updated_at || node.last_heard) && (
                      <div className="node-detail-tile-secondary-line">Updated {relativeTime(node.updated_at || node.last_heard)}</div>
                    )}
                  </>
                ) : estimatedPosition ? (
                  // 2026-09-26 board item ("Estimated position for nodes
                  // without one") — never worded or styled like a real
                  // reported position (see estimatedPosition.method in the
                  // map legend for the "not a reported position" caption);
                  // this is a distinct, muted "Estimated:" line, not the
                  // plain coordinate pair the branch above shows for a
                  // real position.
                  //
                  // 2026-09-26 operator-review fix #4: radius_km
                  // (ESTIMATE_RADIUS_KM) is a single network-wide
                  // calibration figure (position_estimate.py's own LOO
                  // run over the whole mesh), not something measured
                  // per-node — the old "(within ~40 km, 80% of the time)"
                  // phrasing read as if it were this node's own accuracy.
                  // Reworded to say "network-wide" explicitly; both
                  // numbers are live from the API response
                  // (radius_km/coverage_pct, settings-coverage pass part
                  // 4, 2026-09-27 — position_estimate.estimate_radius_km/
                  // estimate_radius_coverage_pct), never a hardcoded
                  // "40 km"/"80%".
                  <div className="node-detail-tile-value node-detail-tile-value-muted" title="Computed from positioned neighbours' signal — not a reported position.">
                    Estimated: {estimatedPosition.lat.toFixed(4)}, {estimatedPosition.lon.toFixed(4)} — network-wide accuracy: within ~
                    {formatDistanceKm(estimatedPosition.radius_km, distPref)} {estimatedPosition.coverage_pct.toFixed(0)}% of the time
                  </div>
                ) : (
                  <div
                    className="node-detail-tile-value node-detail-tile-value-muted"
                    title={
                      node.lat === 0 && node.lon === 0
                        ? "MeshCore/Meshtastic report (0, 0) when the device has no GPS fix — not a real location."
                        : undefined
                    }
                  >
                    {node.lat === 0 && node.lon === 0 ? "No position reported" : "unknown"}
                  </div>
                )}
              </div>
              <InfoTile label="Elevation" value={node.alt != null ? `${node.alt}m` : null} theme={effectiveTheme} />

              {/* nodes.channel is TEXT fed from meshview's roster string — a
                  different fact from Meshtastic's own integer local-channel
                  index, sharing only the column name. Labelled to say so via
                  the tile's tooltip, same caveat the old row carried. */}
              <InfoTile
                label="Channel"
                value={node.channel}
                title="From meshview's roster string — not Meshtastic's own local-channel index."
                theme={effectiveTheme}
              />
              {node.is_mqtt_gateway === true && (
                <InfoTile label="MQTT gateway" value="Confirmed" title="Reported as an MQTT uplink gateway by an observer." theme={effectiveTheme} />
              )}

              <InfoTile label="Public key" value={node.public_key} wide theme={effectiveTheme} />

              <InfoTile label="First seen" value={node.first_seen ? relativeTime(node.first_seen) : null} theme={effectiveTheme} />
              <InfoTile label="Last heard" value={node.last_heard ? lastHeardText(relativeTime(node.last_heard), node.last_heard_source_label) : null} theme={effectiveTheme} />
              <InfoTile label="Record updated" value={node.updated_at ? relativeTime(node.updated_at) : null} theme={effectiveTheme} />
            </div>
          </SectionCard>

          {protocol === "meshcore" && (
            <ContactShareSection name={node.long_name || node.short_name} publicKey={node.public_key || (/^[0-9a-f]{64}$/i.test(String(node.id)) ? String(node.id) : null)} roleKey={role?.key} />
          )}

          {/* Clock offset/drift (2026-09-26, "Clock drift per node" board
              item — clock drift is a valuable metric to parse).
              MeshCore-only; renders nothing for any other protocol/
              "unavailable" fetch, same self-managed pattern as
              NodeCommandsSection/ReconciliationSection below. No colour on
              drift (only the offset-magnitude policy is coloured —
              see audit/clock.py's own module docstring for why no firmware
              consequence was found tied to a drift RATE). */}
          {protocolForHistory === "meshcore" && auditClock && auditClock !== "unavailable" && (
            <SectionCard
              title="Clock"
              description="This node's own advert clock, compared against trusted receipt time (our own server's clock, never a third-party observer's)."
            >
              {auditClock.clock_sample_count == null || auditClock.clock_offset_s == null ? (
                <div className="empty-state">
                  Not enough adverts yet to measure this node's clock
                  {auditClock.clock_sample_count != null && ` (${auditClock.clock_sample_count} so far)`}.
                </div>
              ) : (
                <div className="node-detail-tiles">
                  <div className="node-detail-tile">
                    <div className="node-detail-tile-label">Offset</div>
                    <div className="node-detail-tile-value">
                      <span
                        className="badge-outline"
                        style={{
                          color: colorFor(auditColorInfo(auditClock.clock_offset_color).color ?? CHROME.inkMuted, effectiveTheme),
                          // 2026-09-26 check-fixes third pass, fix 2: the
                          // "not set since power-up" wording is far longer
                          // than any other .badge-outline user (that class's
                          // own nowrap is right for a short "X min ahead" —
                          // wrong for this one long sentence, which needs to
                          // wrap instead of clipping against the tile edge).
                          whiteSpace: auditClock.clock_not_set_since_power_up ? "normal" : "nowrap",
                        }}
                      >
                        {/* 2026-09-26 check-fixes batch 1 ("CLOCK NOT SET
                            SINCE POWER-UP TOO NARROW"): a node whose clock
                            has never been set since its last reset reads
                            like "2.2 years behind" — a real number, but one
                            that looks like a bug rather than what it is
                            (an RTC-less board with no clock source at all).
                            Same colour either way (per policy — see
                            audit/clock.py's own citation), just honest
                            wording, with the implied time since power-up
                            (device_ts - RTC_RESET_SENTINEL) spelled out. */}
                        {auditClock.clock_not_set_since_power_up
                          ? `Clock not set since power-up (${formatUptimeSincePowerUp(auditClock.clock_not_set_uptime_s)}; MeshCore starts at 15 May 2024)`
                          : formatClockOffset(auditClock.clock_offset_s)}
                      </span>
                    </div>
                    {auditClock.clock_sample_count != null && (
                      <div className="node-detail-tile-secondary-line">
                        from {auditClock.clock_sample_count} advert{auditClock.clock_sample_count === 1 ? "" : "s"}
                        {auditClock.clock_sample_days != null && ` spanning ${formatSampleSpan(auditClock.clock_sample_days)}`}
                      </div>
                    )}
                  </div>
                  <div className="node-detail-tile">
                    <div className="node-detail-tile-label">Drift</div>
                    <div className="node-detail-tile-value">
                      {auditClock.clock_drift_s_per_day != null
                        ? formatClockDrift(auditClock.clock_drift_s_per_day)
                        : `no measurable drift (${auditClock.clock_sample_count} advert${auditClock.clock_sample_count === 1 ? "" : "s"}${
                            auditClock.clock_sample_days != null ? ` spanning ${formatSampleSpan(auditClock.clock_sample_days)}` : ""
                          })`}
                    </div>
                  </div>
                  {/* 2026-09-26 check-fixes third pass, fix 5 ("CLOCK EVENTS
                      TILE"): "Clock resets seen" and "Clock set" merged into
                      one tile — they are the same underlying concept (a
                      step in this node's own advert clock), just two
                      different firmware-sourced causes (see clock.py's own
                      module docstring, 2c). */}
                  <div className="node-detail-tile">
                    <div className="node-detail-tile-label">Clock events</div>
                    <div className="node-detail-tile-value">
                      {(auditClock.clock_resets ?? 0) === 0 && (auditClock.clock_sets ?? 0) === 0
                        ? "none seen"
                        : [
                            (auditClock.clock_resets ?? 0) > 0
                              ? `${auditClock.clock_resets} reset${auditClock.clock_resets === 1 ? "" : "s"} on power-up`
                              : null,
                            (auditClock.clock_sets ?? 0) > 0
                              ? `${auditClock.clock_sets} set${auditClock.clock_sets === 1 ? "" : "s"} by hand`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                    </div>
                    <div className="node-detail-tile-secondary-line">
                      A reset is a backward jump to MeshCore's own firmware reboot signature; a set-by-hand is a forward (or otherwise
                      non-reboot) step — an app/CLI time-set, not drift.
                    </div>
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {/* Change 3 (2026-09-23): act on this node, not just read about it.
              Self-managed like ReconciliationSection below — decides on its
              own whether there's anything to show (a qualifying transport,
              node-subject operations) and renders nothing/a minimal note
              rather than an empty card when there isn't. See
              NodeCommandsSection.tsx for the param-shape detection and
              transport-qualification rules. */}
          <NodeCommandsSection nodeId={id} protocol={protocol} heardBy={heardBy} />

          <SectionCard title="Position" description="Where this node has reported being, over time — plus its direct neighbours, where this protocol reports them.">
            {history === "unavailable" || history === null ? (
              <div className="node-detail-note">
                {history === null && protocolForHistory === undefined
                  ? "Loading…"
                  : "Position history unavailable for this node."}
              </div>
            ) : historyList.length === 0 && positionedNeighbors.length === 0 && !estimatedPosition ? (
              <div className="empty-state">No sightings recorded for this node yet.</div>
            ) : positionPointsSorted.length === 0 && positionedNeighbors.length === 0 && !estimatedPosition ? (
              <div className="empty-state">
                {historyList.length} sighting{historyList.length === 1 ? "" : "s"} recorded, but none carry a position — nothing to
                map.
              </div>
            ) : (
              <>
                <PositionHistoryMap
                  points={positionPointsSorted}
                  neighbors={neighborList}
                  centerLat={isUsablePosition(node.lat, node.lon) ? node.lat : null}
                  centerLon={isUsablePosition(node.lat, node.lon) ? node.lon : null}
                  estimatedPosition={estimatedPosition}
                  nodeName={name}
                  theme={effectiveTheme}
                />
                <div className="position-history-legend">
                  {estimatedPosition && (
                    <span className="position-history-legend-item">
                      <span
                        className="position-history-legend-swatch"
                        style={{ background: "rgba(155, 63, 214, 0.18)", border: "1px dashed rgba(155, 63, 214, 0.9)" }}
                      />
                      Estimated from the {estimatedPosition.neighbour_count} neighbour{estimatedPosition.neighbour_count === 1 ? "" : "s"} with a
                      known position — not a reported position
                    </span>
                  )}
                  <span className="position-history-legend-item">
                    <span
                      className="position-history-legend-swatch position-history-legend-swatch-bar"
                      style={{ background: "linear-gradient(90deg, #c93a2e, #0ca30c)" }}
                    />
                    Older → recent
                  </span>
                  <span className="position-history-legend-item">
                    <span className="position-history-legend-swatch" style={{ background: "var(--ink)", opacity: 0.9 }} />
                    Reception (heard on the air)
                  </span>
                  <span className="position-history-legend-item">
                    <span
                      className="position-history-legend-swatch"
                      style={{ background: "var(--ink)", opacity: 0.35, border: "2px solid var(--ink)" }}
                    />
                    Roster snapshot (device poll, not a live hearing)
                  </span>
                  {positionedNeighbors.length > 0 && (
                    <>
                      <span className="position-history-legend-item">
                        <span
                          className="position-history-legend-swatch position-history-legend-swatch-bar"
                          style={{ background: "#2a78d6" }}
                        />
                        Neighbour, bidirectional — both sides report hearing each other
                      </span>
                      <span className="position-history-legend-item">
                        <span
                          className="position-history-legend-swatch position-history-legend-swatch-bar"
                          style={{ background: "linear-gradient(90deg, #c98500, rgba(201,133,0,0.15))" }}
                        />
                        Neighbour, one-way claim only — bright end is the side claiming the link; hover a marker for who hears whom
                      </span>
                      <span className="position-history-legend-item">
                        <span
                          className="position-history-legend-swatch position-history-legend-swatch-bar"
                          style={{ background: "repeating-linear-gradient(90deg, #898781 0 3px, transparent 3px 7px)" }}
                        />
                        {/* Fix 2 (2026-09-26 operator review): NeighborInfo is a
                            Meshtastic app module — saying "no NeighborInfo claim"
                            on a MeshCore node's legend is meaningless to a MeshCore
                            operator, since MeshCore has no such module at all. This
                            evidence tier is the same weakest-evidence "just an
                            overheard reception" concept on both protocols; only the
                            wording of what it's contrasted against changes. */}
                        {protocol === "meshcore"
                          ? "Neighbour, observed only — heard by an observer; the node itself doesn't report neighbours"
                          : "Neighbour, observed direct link only — no NeighborInfo claim either side, just an overheard reception"}
                      </span>
                      <span className="position-history-legend-item">
                        <span
                          className="position-history-legend-swatch"
                          style={{ background: effectiveTheme === "dark" ? "#ffffff" : "#0b0b0b", border: `2px solid ${effectiveTheme === "dark" ? "#0b0b0b" : "#ffffff"}` }}
                        />
                        This node
                      </span>
                    </>
                  )}
                </div>
                {positionPointsSorted.length > 0 && (
                  <div className="position-history-summary">
                    {positionStationary ? (
                      <>
                        {positionPointsSorted.length} position report{positionPointsSorted.length === 1 ? "" : "s"}, all from the same
                        location
                        {positionRosterCount > 0 &&
                          ` (${positionRosterCount} from roster snapshot${positionRosterCount === 1 ? "" : "s"})`}
                        .
                      </>
                    ) : (
                      <>
                        {positionPointsSorted.length} position point{positionPointsSorted.length === 1 ? "" : "s"} over{" "}
                        {formatDuration(positionSpanMs)}
                        {positionRosterCount > 0 &&
                          ` (${positionRosterCount} from roster snapshot${positionRosterCount === 1 ? "" : "s"}).`}
                        {positionRosterCount === 0 && "."}
                      </>
                    )}
                    {historyList.length > positionPointsSorted.length && (
                      <> {historyList.length - positionPointsSorted.length} additional sighting(s) had no position attached.</>
                    )}
                  </div>
                )}
                {neighborList.length > 0 && (
                  <div className="position-history-summary">
                    {neighborList.length} direct neighbour{neighborList.length === 1 ? "" : "s"}: {neighborTierCounts.bidirectional}{" "}
                    bidirectional, {neighborTierCounts["one-way"]} one-way, {neighborTierCounts["observed-only"]} observed only
                    {unplacedNeighbors.length > 0 && ` (${unplacedNeighbors.length} without a known position — listed below)`}.
                  </div>
                )}
              </>
            )}

            {/* Neighbour data is its own endpoint with its own honest
                states, independent of whether the track above had
                anything to map — rendered regardless of the branch above
                so a MeshCore node (never has position history yet) still
                gets a truthful answer instead of silence. */}
            {neighbors === "unavailable" ? (
              <div className="node-detail-note">Neighbour data unavailable for this node.</div>
            ) : neighbors === null ? null : !neighbors.available ? (
              <div className="node-detail-note">
                Neighbour data not available for this node{neighbors.reason ? ` — ${neighbors.reason}` : "."}
              </div>
            ) : neighborList.length === 0 ? (
              <div className="empty-state">No direct neighbours recorded for this node yet.</div>
            ) : (
              unplacedNeighbors.length > 0 && (
                <div className="node-neighbors-unplaced">
                  <h5>Neighbours without a known position ({unplacedNeighbors.length})</h5>
                  <p className="node-detail-card-desc">
                    We know these links exist but can't place them on the map yet — a different fact from "no such link".
                  </p>
                  <ul className="node-neighbors-unplaced-list">
                    {unplacedNeighbors.map((n) => {
                      const tier = neighborTier(n);
                      const label = n.long_name || n.short_name || n.native_id;
                      return (
                        <li key={n.native_id} onClick={() => navigate(`#/nodes/${encodeURIComponent(n.native_id)}`)}>
                          <span className="badge" style={{ background: NEIGHBOR_TIER_COLOR[tier] }}>
                            {NEIGHBOR_TIER_LABEL[tier]}
                          </span>
                          <span>{label}</span>
                          {/* Phone overflow fix (2026-09-27): a raw 64-hex
                              native_id here ran off the right edge at 390px
                              — same short-id-plus-copy convention as the
                              Node ID tile above (NodeIdValue), instead of
                              printing the full id inline. Clicks on the copy
                              button must not also navigate the row. */}
                          <span onClick={(e) => e.stopPropagation()}>
                            <NodeIdValue id={n.native_id} />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )
            )}
          </SectionCard>

          {protocol === "meshcore" && neighbors !== "unavailable" && neighbors !== null && neighbors.available && (
            <SectionCard
              title="MeshCore neighbours"
              description="Real per-link SNR evidence — CoreScope's per-observation reception detail and our own radio's RX log — not this node's last advert signal."
            >
              {/* Fix 6: the counts below (Count, Evidence's per-resolution
                  breakdown) accumulate from whenever this node's earliest
                  meshcore_links row was first seen — stating that start
                  point up front instead of leaving "counts since when?"
                  unanswered. */}
              {neighbors.links_since && (
                <div className="node-detail-note" style={{ marginBottom: 10 }}>
                  Counts since {formatDateOnly(neighbors.links_since)}.
                </div>
              )}
              {(neighbors.unresolved_ambiguous_count > 0 ||
                neighbors.unresolved_unknown_count > 0 ||
                neighbors.impossible_distance_count > 0) && (
                <div className="node-detail-note">
                  {neighbors.unresolved_ambiguous_count > 0 &&
                    `${neighbors.unresolved_ambiguous_count} relayed reception${neighbors.unresolved_ambiguous_count === 1 ? "" : "s"} couldn't be tied to one repeater (hash-prefix shared by multiple known repeaters).`}
                  {neighbors.unresolved_ambiguous_count > 0 && neighbors.unresolved_unknown_count > 0 && " "}
                  {neighbors.unresolved_unknown_count > 0 &&
                    `${neighbors.unresolved_unknown_count} relayed reception${neighbors.unresolved_unknown_count === 1 ? "" : "s"} named a repeater we don't know at all.`}
                  {(neighbors.unresolved_ambiguous_count > 0 || neighbors.unresolved_unknown_count > 0) &&
                    neighbors.impossible_distance_count > 0 &&
                    " "}
                  {/* 2026-09-26 board item ("Node page: MeshCore neighbours"
                      distance fix) — a link couldn't be tied to one
                      repeater in a DIFFERENT way than the two counts above:
                      it WAS resolved, but the two positioned ends imply a
                      physically-impossible distance — a short-hash-prefix
                      collision, not a real RF link (research report,
                      amalgamesh-db-snapshots/research-2026-09-26/
                      position-estimate.md). Never shown/drawn; counted
                      here instead of silently dropped.

                      2026-09-26 operator-review fix #1: was a bare count
                      with no denominator ("141 receptions implied an
                      impossible distance"), which read as an unexplained,
                      contradictory claim directly above an unrelated
                      "No MeshCore neighbour evidence recorded" empty
                      state when every single reception got set aside.
                      Now one connected sentence naming the denominator
                      (total_receptions_credited) and, when nothing
                      usable is left, saying so explicitly instead of
                      leaving the empty-state message below to repeat/
                      contradict it (see the empty-state gating below,
                      which now suppresses that message in exactly this
                      case). */}
                  {neighbors.impossible_distance_count > 0 &&
                    `${neighbors.impossible_distance_count.toLocaleString()} of ${neighbors.total_receptions_credited.toLocaleString()} reception${neighbors.impossible_distance_count === 1 ? "" : "s"} credited to this node implied an impossible distance (short-ID clash) and were set aside` +
                      (meshcoreHears.length === 0 && meshcoreHeardBy.length === 0
                        ? "; no usable neighbour links remain."
                        : ".")}
                </div>
              )}
              {/* 2026-09-26 operator-review fix #2: for a node with >=1
                  impossible-distance reception, name the other known
                  repeaters/room servers sharing its own 1-byte hash-
                  prefix — the SAME collision population a relayed
                  reception's last hop is resolved against
                  (RelayCandidateIndex.by_width[1], observers/
                  meshcore_links.py, reused not rewritten). A plain
                  statement of fact, never a guess at which specific
                  candidate caused any one bad reading. */}
              {neighbors.impossible_distance_count > 0 && neighbors.short_id_clash_partners.length > 0 && (
                <div className="node-detail-note">
                  Shares its 1-byte short ID with:{" "}
                  {neighbors.short_id_clash_partners.map((partner, i) => (
                    <span key={partner.native_id}>
                      {i > 0 && ", "}
                      {partner.long_name || partner.native_id} ({formatDistanceKm(partner.distance_km, distPref)} away)
                    </span>
                  ))}
                  .
                </div>
              )}
              {meshcoreHears.length === 0 && meshcoreHeardBy.length === 0 ? (
                neighbors.impossible_distance_count === 0 && (
                  <div className="empty-state">No MeshCore neighbour evidence recorded for this node yet.</div>
                )
              ) : (
                <>
                  <MeshCoreLinkTable
                    title="Hears (this node received their transmission)"
                    rows={meshcoreHears}
                    emptyReason="this node isn't one of CoreScope's observer stations and isn't our own connected radio, so it never reports what it heard."
                  />
                  <MeshCoreLinkTable
                    title="Heard by (they received this node's transmission)"
                    rows={meshcoreHeardBy}
                    emptyReason="no observer station or our own radio has reported hearing this node's transmission yet."
                  />
                </>
              )}
            </SectionCard>
          )}

          {protocol === "meshcore" && (role?.key === "repeater" || role?.key === "room_server") && (
            <NodeReachSection
              nodeId={id}
              nodeName={name}
              position={isUsablePosition(node.lat, node.lon) ? { lat: node.lat as number, lon: node.lon as number } : null}
              theme={effectiveTheme}
              distPref={distPref}
            />
          )}

          {protocol === "meshcore" && <NodeAuditSection nodeId={id} />}

          {(protocol === "meshcore" || protocol === "meshtastic") && <NodeLocationSection nodeId={id} protocol={protocol} />}

          <SectionCard title="Telemetry" description="Telemetry readings over time, plus the longer stored history behind them.">
            {telemetryHistory === "unavailable" ? (
              <div className="node-detail-note">Telemetry history unavailable for this node.</div>
            ) : telemetryHistory === null ? (
              <div className="node-detail-note">Loading…</div>
            ) : (
              <>
                {/* One chart per metric field ("telemetry needs to be nice
                    looking graphs, not a string of cards. ONE graph for ONE
                    type of telemetry a dot graph with lines to see trends" —
                    verbatim). Built from the exact same GET .../telemetry
                    fetch the history table below already uses — see
                    buildTelemetrySeries above for the raw-preferred/
                    hourly-fallback pivot and the multi-observer merge. */}
                {telemetrySeriesSorted.length === 0 ? (
                  <div className="empty-state">No telemetry recorded for this node yet.</div>
                ) : (
                  <div className="telemetry-charts-grid">
                    {telemetrySeriesSorted.map(([field, s]) => {
                      const unit = displayTelemetryUnit(field, tempPref);
                      const latest = s.points[s.points.length - 1].v;
                      return (
                        <TelemetryChart
                          key={field}
                          title={`${titleizeTelemetryField(field)}${unit ? ` (${unit})` : ""}`}
                          latestLabel={formatTelemetryValue(field, latest, tempPref)}
                          points={s.points}
                          theme={effectiveTheme}
                          formatValue={(v) => formatTelemetryValue(field, v, tempPref)}
                          formatTime={chartTimeLabel}
                          yDomain={isPercentTelemetryField(field) ? [0, 100] : undefined}
                        />
                      );
                    })}
                  </div>
                )}

                {/* GET /api/history/nodes/{protocol}/{native_id}/telemetry —
                    every raw + hourly-rollup telemetry column, condensed
                    client-side (summarizeTelemetryHistory) into one
                    latest-value-per-field table per metric_type. Kept
                    alongside the charts above — useful on its own (last
                    updated / since / sample count per field), not
                    superseded by them. */}
                <div className="radio-snapshot-subsection">
                  <h5>Telemetry history</h5>
                  {!telemetrySummary || telemetrySummary.size === 0 ? (
                    <div className="empty-state">No telemetry history recorded for this node yet.</div>
                  ) : (
                    Array.from(telemetrySummary.entries()).map(([metricType, fields]) => (
                      <div key={metricType} style={{ marginBottom: 12 }}>
                        <div className="telemetry-history-metric-type">{metricType}</div>
                        <div className="telemetry-history-wrap">
                          <table className="telemetry-history-table">
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Latest</th>
                                <th>Last updated</th>
                                <th>Since</th>
                                <th>Samples</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.from(fields.entries()).map(([field, s]) => (
                                <tr key={field}>
                                  <td>{titleizeTelemetryField(field)}</td>
                                  <td>{formatTelemetryValue(field, s.latest, tempPref)}</td>
                                  <td>{relativeTime(s.latestAt)}</td>
                                  <td>{relativeTime(s.earliestAt)}</td>
                                  <td>{s.sampleCount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </SectionCard>

          {/* Self-managed, same as NodeCommandsSection above — a node with
              no reconciliation links (the common case) renders nothing here
              rather than a full card saying "nothing to merge" (Change 1). */}
          <ReconciliationSection nodeId={id} protocol={protocolForHistory} isAdmin={isAdmin} />

          <SectionCard title="Recent packets" description="The most recent packets recorded from this node (server-side capped).">
            {recentPackets.length === 0 ? (
              <div className="empty-state">No packets recorded from this node yet.</div>
            ) : (
              <ul className="recent-packet-list">
                {recentPackets.map((p, i) => {
                  const cat = packetCategoryInfo(p.protocol, p.type);
                  return (
                    <li key={`${p.hash || p.id || "pkt"}-${i}`} className="recent-packet-row-full" onClick={() => select({ kind: "packet", packet: p })}>
                      <span className="badge" style={{ background: colorFor(cat.color, effectiveTheme) }}>
                        {cat.label}
                      </span>
                      <span>{relativeTime(p.ts)}</span>
                      <ObserverBadge observerId={p.observer_id ?? "unknown"} trustTier={p.trust_tier} compact />
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          {/* Position history list (Change 1): the textual log portion split
              out of the map card above and placed last — logs at the
              bottom, the map/telemetry that people actually want stays up
              top. Same rows as before, just relocated. */}
          {positionPointsSorted.length > 0 && (
            <SectionCard title="Position history" description="The most recent position reports behind the map above.">
              <ul className="position-history-recent-list">
                {recentPositionPoints.map((p, i) => {
                  const ts = p.ts ?? p.observed_at;
                  const kind = p.sighting_kind ?? undefined;
                  return (
                    <li key={i}>
                      <span
                        className="badge sighting-kind-badge"
                        style={{ background: (kind && SIGHTING_KIND_COLOR[kind]) || "var(--ink-muted)" }}
                        title={(kind && SIGHTING_KIND_TITLE[kind]) || undefined}
                      >
                        {(kind && SIGHTING_KIND_LABEL[kind]) || kind || "unknown"}
                      </span>
                      <span>{clockTime(ts)}</span>
                      <span>
                        {p.lat!.toFixed(4)}, {p.lon!.toFixed(4)}
                      </span>
                      {p.observer_id && <ObserverBadge observerId={p.observer_id} trustTier={observerTrust.get(p.observer_id)} compact />}
                    </li>
                  );
                })}
              </ul>
            </SectionCard>
          )}
        </div>

        <aside className="node-detail-aside">
          <h3 className="node-detail-aside-header">Our observers</h3>
          <p className="node-detail-aside-note">
            The sources that heard this node — our own radio, MQTT relays, external feeds. This is not the same thing as
            node-to-node neighbours (who this node can hear over the air) — those are drawn on the map above, in the Position
            section.
          </p>
          {heardBy.length === 0 && csStations.length === 0 ? (
            <div className="empty-state">No observer has reported this node.</div>
          ) : (
            <ul className="observer-sighting-tiles">
              {heardBy.map((h, i) => (
                <li key={`${h.observer_id}-${i}`} className="observer-sighting-tile">
                  {/* Per-sighting trust_tier (node_sightings.trust_tier, now sent
                      by the backend, 2026-09-23) is more honest than the
                      observer's configured default tier, since a given
                      observer's trust can vary sighting to sighting — prefer
                      it, falling back to the app-wide registry only when a
                      build hasn't sent it. */}
                  <ObserverBadge observerId={h.observer_id} trustTier={h.trust_tier ?? observerTrust.get(h.observer_id)} />
                  <div className="observer-sighting-tile-meta">
                    {h.count != null && <span>{h.count} packet(s)</span>}
                    {h.last_heard && <span>last {relativeTime(h.last_heard)}</span>}
                  </div>
                </li>
              ))}
              {csStations.map((c) => (
                <li key={`cs-${c.station_id}`} className="observer-sighting-tile">
                  <span className="observer-badge" data-verified="false" title={`${c.name || c.station_id} (${c.station_id})`}>
                    <span className="observer-badge-id">{c.name || c.station_id}</span>
                    <span className="observer-badge-tier">CoreScope</span>
                  </span>
                  <div className="observer-sighting-tile-meta">
                    <span>{c.count} packet(s)</span>
                    <span>last {relativeTime(c.last_heard)}</span>
                    {c.best_snr != null && <span>best SNR {c.best_snr} dB</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {heardBy.length === 1 && csStations.length === 0 && (
            <div className="node-detail-note">
              Only one observer has reported this node so far — external observers (CoreScope, meshview) may simply not have
              seen it, which itself can be a coverage-gap signal rather than a missing feature.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
